import React from 'react';
import PropTypes from 'prop-types';
import '../../../styles/index.css';

/**
 * ChartCard - Shared wrapper component for all charts
 * 
 * Provides consistent styling, loading states, and error handling.
 * Replaces duplicate .Floating-panel, .Bdm-panel, .panel-content structures.
 * 
 * @example
 * <ChartCard 
 *   title="Monthly Revenue"
 *   loading={isLoading}
 *   error={error}
 *   height={400}
 * >
 *   <MyChart data={data} />
 * </ChartCard>
 */
const ChartCard = ({
  children,
  title,
  subtitle,
  loading = false,
  error = null,
  height = 'auto',
  className = '',
  headerActions = null,
  fullWidth = false,
}) => {
  // Calculate container classes
  const containerClasses = [
    'chart-card',
    fullWidth ? 'chart-card--full-width' : '',
    typeof height === 'number' ? 'chart-card--fixed-height' : '',
    className,
  ].filter(Boolean).join(' ');

  // Loading state
  if (loading) {
    return (
      <div className={containerClasses}>
        <div className="chart-card__header">
          {title && <h3 className="chart-card__title">{title}</h3>}
        </div>
        <div 
          className="chart-card__content chart-card__content--loading"
          style={{ height: typeof height === 'number' ? `${height}px` : height }}
        >
          <div className="chart-card__spinner">
            <div className="spinner"></div>
            <p>Loading chart...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={containerClasses}>
        <div className="chart-card__header">
          {title && <h3 className="chart-card__title">{title}</h3>}
        </div>
        <div 
          className="chart-card__content chart-card__content--error"
          style={{ height: typeof height === 'number' ? `${height}px` : height }}
        >
          <div className="chart-card__error">
            <svg 
              className="chart-card__error-icon"
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
              />
            </svg>
            <p className="chart-card__error-title">Unable to load chart</p>
            <p className="chart-card__error-message">
              {typeof error === 'string' ? error : 'An error occurred while loading the data.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Normal state with chart content
  return (
    <div className={containerClasses} style={{ minHeight: typeof height === 'number' ? `${height + 100}px` : 'auto' }}>
      {(title || subtitle || headerActions) && (
        <div className="chart-card__header">
          <div className="chart-card__header-content">
            {title && <h3 className="chart-card__title">{title}</h3>}
            {subtitle && <p className="chart-card__subtitle">{subtitle}</p>}
          </div>
          {headerActions && (
            <div className="chart-card__actions">
              {headerActions}
            </div>
          )}
        </div>
      )}
      <div 
        className="chart-card__content"
        style={{ 
          height: typeof height === 'number' ? `${height + 48}px` : height, // Increased from 40px to 48px (24px top + 24px bottom) for better breathing room
          boxSizing: 'border-box'
        }}
      >
        {children}
      </div>
    </div>
  );
};

ChartCard.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  headerActions: PropTypes.node,
  fullWidth: PropTypes.bool,
};

export default ChartCard;
